import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { useAuthenticationDataContext } from '../../Helpers/AuthenticationDataProvider';
import { useAuthorizationDataContext } from '../../Helpers/AuthorizationDataProvider';
import { useIntlWrapperContext } from '../../Helpers/IntlWrapperProvider';
import useFetchWrapper, { HttpResponse } from '../../Helpers/useFetchWrapper';
import useScoreHelper, { Score } from '../../Helpers/useScoreHelper';
import { WebSocketService } from '../../Helpers/WebSocketProvider';

import {
	ContactStatus,
	ContactStatusResponse,
	Review,
	UserProfileModel,
	UserProfileResponse,
	UserRatingResponse,
} from './UserProfile.model';
import { ReceivedContactRequest } from '../../components/ContactDropdown/ContactDropdown.model';

const useUserProfile = (props: { id: string }) => {
	const [activeTab, setActiveTab] = useState('1');
	const [averageScore, setAverageScore] = useState<Score | undefined>();
	const [profile, setProfile] = useState<UserProfileModel>();
	const [profileResponse, setProfileResponse] = useState<UserProfileResponse>();
	const [reviews, setReviews] = useState<Review[]>([]);
	const [rocketRoomId, setRocketRoomId] = useState<string>('');
	const [showContactPopup, setShowContactPopup] = useState(false);
	const [showRatingPopup, setShowRatingPopup] = useState(false);
	const [showRejectPopup, setShowRejectPopup] = useState(false);
	const [showScheduleEventPopup, setShowScheduleEventPopup] = useState(false);
	const [contactStatus, setContactStatus] = useState<ContactStatus>(ContactStatus.SendRequest);
	const [showJoinCall, setShowJoinCall] = useState(false);
	const [mentors, setMentors] = useState<any[]>();

	const { id } = props;

	const { get, post, put } = useFetchWrapper();
	const { rocketAuthToken, userId } = useAuthenticationDataContext();
	const { isAuthorized } = useAuthorizationDataContext();
	const { i18nCountries, getI18nLanguageName } = useIntlWrapperContext();
	const intl = useIntl();
	const history = useHistory();
	let { url } = useRouteMatch();
	const { getScoreByAverageRating } = useScoreHelper();
	const { closeWS, initializeWS, state: {message} } = WebSocketService();

	useEffect(() => {
		setActiveTab('1');
	}, [url]);

	useEffect(() => {
		getUserProfile();
		getUserRating();
		getContactStatus();
		getMentors();
	}, [id]);

	useEffect(() => {
		if (message && message?.MeetingState?.state.find(state => state.user !== userId && state.joined)) {
			setShowJoinCall(true);
		}
		else {
			setShowJoinCall(false);
		}
	}, [message]);

	useEffect(() => {
		return () => { closeWS(userId, rocketRoomId) };
	},[])

	useEffect(() => {
		profile?.role === 'Mentor' && getUserRating();
	}, [profile]);

	const formatDate = (date: string) => {
		return intl.formatDate(new Date(date), { month: 'long', year: 'numeric' });
	}

	const getMentors = async () => {
		const response: HttpResponse<any> = await get('/user/all-types?role=Mentor');
		response.parsedBody && setMentors(response.parsedBody);
	}

	const getUserRating = async () => {
		const response: HttpResponse<UserRatingResponse> = await get(`/review/rating/${id}?page=1&size=5`);

		if (response.parsedBody) {
			const { averageScore, ratings } = response.parsedBody;

			setReviews(
				ratings.map(({ feedback, score, user }) => ({
					feedback,
					rating: score,
					reviewer: {
						imageId: user.imageId,
						isDeleted: user.isDeleted,
						name: `${user.firstName} ${user.lastName}`,
						userId: user.userId,
					},
				}))
			);

			setAverageScore(getScoreByAverageRating(averageScore));
		}
	}

	const getUserProfile = async () => {
		const response: HttpResponse<UserProfileResponse> = await get(`/user/profile/${id}`);
		if(response.status === 403) return history.push("/");
		if (response.parsedBody) {
			const {
				currentPosition,
				education,
				firstName,
				imageId,
				introduction,
				isDeleted,
				isReachableByCall,
				isReachableByText,
				languages,
				lastName,
				location,
				mentoringScopes,
				pronouns,
				researchAreas,
				role,
				workExperience,
			} = response.parsedBody;
			
			if(isDeleted) return history.push("/");

			const user: UserProfileModel = {
				currentPosition,
				education,
				firstName,
				imageId,
				introduction,
				isReachableByCall,
				isReachableByText,
				languages: languages?.map((code) => getI18nLanguageName(code)),
				lastName,
				location,
				mentoringScopes,
				name: `${firstName} ${lastName} `,
				pronouns,
				researchAreas,
				role,
				workExperience,
			};

			setProfile(user);
			setProfileResponse(response.parsedBody)
		}
	}

	const getContactStatus = async () => {
		const response: HttpResponse<ContactStatusResponse> = await get(`/communication/${id}`);

		if (response.parsedBody) {
			const { communicationStatus, rocketRoomId } = response.parsedBody;
			setContactStatus(communicationStatus);
			setRocketRoomId(rocketRoomId);
			initializeWS(rocketRoomId, userId);
		}
	}

	const sendContactRequest = async (formData: any) => {
		await post(`/communication/${id}`, formData);
		getContactStatus();
	}

	const getContactRequestId = async () => {
		const response: HttpResponse<ReceivedContactRequest[]> = await get(`/communication/to/pending?page=1&size=100`);
		return response.parsedBody?.find(request => request.userId === id)?.chatRequestId;
	}

	const acceptContactRequest = async () => {
		const requestId = await getContactRequestId();
		await put(`/communication/${requestId}/accept`, {});
		getContactStatus();
	}

	const navigateTo = (path: string, state?: any, search?: string) => {
		history.push({ pathname: path, search, state });
	}

	const onClickJoinMeeting = async () => {
		const response = await get<string>(`/calendar/direct-call/${rocketRoomId}`);

		if (response.parsedBody) {
			const meetUrl: string = response.parsedBody;

			window.open(meetUrl, "_self")?.focus();
		}
	}

	const onClickReviewer = (id: string) => {
		const path = userId !== id ? `/members-list/${id}` : '/my-profile';
    navigateTo(path);
	}

	const onClickStartMeeting = async () => {
		const response = await post<string>(`/calendar/direct-call/${rocketRoomId}`, {});

		if (response.parsedBody) {
			const meetUrl: string = response.parsedBody;

			window.open(meetUrl, "_self")?.focus();
		}
	}

	const onChangeTab = (activeKey: string) => {
		setActiveTab(activeKey);
	}

	const rejectContactRequest = async (formData: { rejectionMessage: string }) => {
		const requestId = await getContactRequestId();
		await put(`/communication/${requestId}/reject`, formData);
		getContactStatus();
	}

	const togglePopup = (setStateFunction: React.Dispatch<React.SetStateAction<boolean>>) => {
		setStateFunction((currentValue: boolean) => !currentValue);
	}

	return {
		acceptContactRequest,
		formatDate,
		getUserProfile,
		isAuthorized,
		navigateTo,
		onChangeTab,
		onClickJoinMeeting,
		onClickReviewer,
		onClickStartMeeting,
		rejectContactRequest,
		sendContactRequest,
		setActiveTab,
		state: {
			activeTab,
			averageScore,
			contactStatus,
			i18nCountries,
			mentors,
			profile,
			profileResponse,
			reviews,
			rocketAuthToken,
			rocketRoomId,
			showContactPopup,
			showJoinCall,
			showRatingPopup,
			showRejectPopup,
			showScheduleEventPopup,
		},
		toggleContactPopup: () => {
			togglePopup(setShowContactPopup);
		},
		toggleRatingPopup: () => {
			window.scrollTo(0, 0);
			togglePopup(setShowRatingPopup);
		},
		toggleRejectPopup: () => {
			togglePopup(setShowRejectPopup);
		},
		toggleScheduleEventPopup: () => {
			togglePopup(setShowScheduleEventPopup);
		},
		userId,
	}

}
export default useUserProfile;